import { useField } from 'formik';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import AcademiaCountryCodeSelect from './AcademiaCountryCodeSelect';

interface IAcademiaInputField {
  type: 'text' | 'email' | 'number' | 'password';
  placeholder: string;
  label: string;
  name: string;
  defaultSelectedCountryCode?: string;
  setCountryCode?: React.Dispatch<React.SetStateAction<string>>;
  badge?: React.ReactNode;
  isDisabled?: boolean;
  isRequired?: boolean;
  [key: string]: any;
}

const AcademiaInputField = ({
  name,
  type,
  label,
  placeholder,
  countries,
  defaultSelectedCountryCode,
  setCountryCode,
  badge,
  isDisabled,
  isRequired,
  ...props
}: IAcademiaInputField) => {
  // integration of react hooks here
  const [showPassword, setShowPassword] = useState<boolean>(false);

  // integration of formik hooks here
  const [field, meta] = useField(name);

  // rendering custom input field component here
  return (
    <div>
      <label
        htmlFor={`input-${name}`}
        title={isRequired ? 'This field is required' : ''}
        className='block mb-1 text-legal-dark-blue font-medium text-base'
      >
        {label} {isRequired && <span className='text-base-red'>*</span>}
      </label>

      <motion.div
        initial={{ x: 0 }}
        animate={
          meta.touched && meta.error
            ? {
                x: [0, -10, 0, 10, 0],
                transition: {
                  duration: 0.1,
                  repeat: 2,
                },
              }
            : { x: 0 }
        }
        className='relative'
      >
        <div className='flex flex-col md:flex-row items-end gap-3'>
          {setCountryCode ? (
            <AcademiaCountryCodeSelect
              setCountryCode={setCountryCode}
              defaultSelectedCountryCode={defaultSelectedCountryCode}
            />
          ) : null}

          <input
            id={`input-${name}`}
            type={
              type === 'password' ? (showPassword ? 'text' : 'password') : type
            }
            className={`border-2 focus:ring-4 focus:ring-legal-dark-blue outline-legal-dark-blue rounded-md w-full p-3 text-legal-dark-blue font-semibold duration-300 text-base disabled:opacity-40 disabled:cursor-not-allowed read-only:opacity-60 read-only:focus:ring-0 ${
              meta.touched && meta.error
                ? 'border-base-red'
                : 'border-legal-dark-blue'
            }`}
            placeholder={placeholder}
            onWheel={(event) => event.currentTarget.blur()}
            disabled={isDisabled}
            autoComplete='off'
            {...field}
            {...props}
          />
        </div>

        {badge && badge}

        {type === 'password' && (
          <button
            tabIndex={-1}
            type='button'
            onClick={() => setShowPassword((state) => !state)}
            className='absolute right-4 top-1/2 -translate-y-1/2 text-legal-dark-blue/70 hover:opacity-70 duration-300'
          >
            {showPassword ? (
              <FaEyeSlash className='h-5 w-5' />
            ) : (
              <FaEye className='h-5 w-5' />
            )}
          </button>
        )}
      </motion.div>

      <motion.label
        initial={{ y: -20, opacity: 0, display: 'none' }}
        animate={
          meta.touched && meta.error
            ? { y: 0, opacity: 1, display: 'block' }
            : { y: -20, opacity: 0, transitionEnd: { display: 'none' } }
        }
        htmlFor={`input-${name}`}
        className='block mt-1 text-base-red text-base'
      >
        {meta.error}
      </motion.label>
    </div>
  );
};

export default AcademiaInputField;
